/**
 * The search filters, which live only in the URL (issue #139). Pure helpers, no
 * imports, so they can be tested with `node --test`.
 */

export const SORTS = ["newest", "price_asc", "price_desc"] as const;
export type Sort = (typeof SORTS)[number];

export const PAGE_SIZE = 24;
/** The API shows at most the first 1,000 matches (`page * pageSize` above it is a 422). */
export const MAX_RESULTS = 1000;
export const MAX_PAGE = Math.floor(MAX_RESULTS / PAGE_SIZE);

export type Filters = {
    /** An MLS® number search (issue #223): when set it replaces every other filter. */
    mls?: string;
    area?: string;
    subdivision?: string;
    minPrice?: number;
    maxPrice?: number;
    beds?: number;
    baths?: number;
    type?: string;
    sort: Sort;
    page: number;
};

type Raw = Record<string, string | string[] | undefined>;

function first(value: string | string[] | undefined): string | undefined {
    return Array.isArray(value) ? value[0] : value;
}

function slug(value: string | undefined): string | undefined {
    const v = value?.trim().toLowerCase();
    return v && /^[a-z0-9]+(-[a-z0-9]+)*$/.test(v) && v.length <= 100 ? v : undefined;
}

/** A visitor's MLS® number: trimmed, upper-cased, 1-32 letters, digits and hyphens; else undefined. */
function mlsNumber(value: string | undefined): string | undefined {
    const v = value?.trim().toUpperCase();
    return v && /^[A-Z0-9-]{1,32}$/.test(v) ? v : undefined;
}

function whole(value: string | undefined, max: number): number | undefined {
    const v = value?.trim();
    if (!v || !/^\d{1,9}$/.test(v)) return undefined;
    const n = Number(v);
    return n <= max ? n : undefined;
}

/**
 * The search's starting choices staff set in Settings (issue #181): objective filters only
 * (CREA rule 7), never an office or agent. Each is used when the URL doesn't say otherwise.
 */
export type SearchDefaults = {
    area?: string;
    type?: string;
    minPrice?: number;
    maxPrice?: number;
    sort: Sort;
};

/** No starting choices: every area and type, any price, newest first. */
export const NO_DEFAULTS: SearchDefaults = {sort: "newest"};

/** The words in the URL for "the visitor removed this default": `area=all`, `minPrice=any`. */
const ALL = "all";
const ANY = "any";

/** Reads the starting choices from the public settings (strings); anything invalid is dropped. */
export function defaultsFromSettings(values: {
    listing_search_default_area?: string;
    listing_search_default_type?: string;
    listing_search_default_min_price?: string;
    listing_search_default_max_price?: string;
    listing_search_default_sort?: string;
}): SearchDefaults {
    const sort = values.listing_search_default_sort;
    return {
        area: slug(values.listing_search_default_area),
        type: slug(values.listing_search_default_type),
        minPrice: whole(values.listing_search_default_min_price, 100_000_000),
        maxPrice: whole(values.listing_search_default_max_price, 100_000_000),
        sort: (SORTS as readonly string[]).includes(sort ?? "") ? (sort as Sort) : "newest",
    };
}

/**
 * Reads the filters from the URL's query; anything invalid is dropped, never an error. A filter
 * the URL leaves out takes its starting choice from `defaults`; `area=all`, `type=all`,
 * `minPrice=any` and `maxPrice=any` say "no filter" even when a starting choice exists.
 */
export function parseFilters(raw: Raw, defaults: SearchDefaults = NO_DEFAULTS): Filters {
    // An MLS® number replaces every other filter, and the starting choices are not applied.
    const mls = mlsNumber(first(raw.mls));
    if (mls) return {mls, sort: defaults.sort, page: 1};
    const sort = first(raw.sort);
    const rawArea = first(raw.area)?.trim().toLowerCase();
    const rawType = first(raw.type)?.trim().toLowerCase();
    const rawMin = first(raw.minPrice)?.trim().toLowerCase();
    const rawMax = first(raw.maxPrice)?.trim().toLowerCase();
    // Absent: the starting choice. All/any (or an empty field of the plain form): none. Otherwise
    // the value (an invalid one is dropped).
    const area = rawArea === undefined ? defaults.area : rawArea === ALL ? undefined : slug(rawArea);
    const type = rawType === undefined ? defaults.type : rawType === ALL ? undefined : slug(rawType);
    let minPrice = rawMin === undefined ? defaults.minPrice : rawMin === ANY ? undefined : whole(rawMin, 100_000_000);
    let maxPrice = rawMax === undefined ? defaults.maxPrice : rawMax === ANY ? undefined : whole(rawMax, 100_000_000);
    if (minPrice !== undefined && maxPrice !== undefined && minPrice > maxPrice) {
        [minPrice, maxPrice] = [maxPrice, minPrice];
    }
    const page = whole(first(raw.page), MAX_PAGE) ?? 1;
    return {
        area,
        subdivision: slug(first(raw.subdivision)),
        minPrice,
        maxPrice,
        beds: whole(first(raw.beds), 20),
        baths: whole(first(raw.baths), 20),
        type,
        sort: (SORTS as readonly string[]).includes(sort ?? "") ? (sort as Sort) : defaults.sort,
        page: Math.max(1, page),
    };
}

/**
 * The query string of the filters: only what differs from the starting choices is written, so
 * URLs stay short and the plain `/listings` means "the starting choices". A starting choice the
 * visitor removed is written as `all` or `any`, so it survives paging and sharing.
 */
export function toQuery(
    filters: Filters,
    overrides: Partial<Filters> = {},
    defaults: SearchDefaults = NO_DEFAULTS,
): URLSearchParams {
    const f = {...filters, ...overrides};
    const q = new URLSearchParams();
    if (f.mls) {
        q.set("mls", f.mls);
        return q;
    }
    if (f.area !== defaults.area) q.set("area", f.area ?? ALL);
    if (f.subdivision) q.set("subdivision", f.subdivision);
    if (f.minPrice !== defaults.minPrice) q.set("minPrice", f.minPrice === undefined ? ANY : String(f.minPrice));
    if (f.maxPrice !== defaults.maxPrice) q.set("maxPrice", f.maxPrice === undefined ? ANY : String(f.maxPrice));
    if (f.beds) q.set("beds", String(f.beds));
    if (f.baths) q.set("baths", String(f.baths));
    if (f.type !== defaults.type) q.set("type", f.type ?? ALL);
    if (f.sort !== defaults.sort) q.set("sort", f.sort);
    if (f.page > 1) q.set("page", String(f.page));
    return q;
}

/** The API's search query: the effective filters (never `all` or `any`) plus the page size. */
export function toApiQuery(filters: Filters): URLSearchParams {
    const q = new URLSearchParams();
    if (filters.mls) {
        q.set("mls", filters.mls);
        q.set("pageSize", String(PAGE_SIZE));
        return q;
    }
    if (filters.area) q.set("area", filters.area);
    if (filters.subdivision) q.set("subdivision", filters.subdivision);
    if (filters.minPrice !== undefined) q.set("minPrice", String(filters.minPrice));
    if (filters.maxPrice !== undefined) q.set("maxPrice", String(filters.maxPrice));
    if (filters.beds) q.set("beds", String(filters.beds));
    if (filters.baths) q.set("baths", String(filters.baths));
    if (filters.type) q.set("type", filters.type);
    if (filters.sort !== "newest") q.set("sort", filters.sort);
    if (filters.page > 1) q.set("page", String(filters.page));
    q.set("pageSize", String(PAGE_SIZE));
    return q;
}

/** The site's `/listings` address for the filters, relative to the starting choices. */
export function listingsHref(
    filters: Filters,
    overrides: Partial<Filters> = {},
    defaults: SearchDefaults = NO_DEFAULTS,
): string {
    const q = toQuery(filters, overrides, defaults).toString();
    return q ? `/listings?${q}` : "/listings";
}

/**
 * True when the URL has anything beyond the plain `/listings`: a filter or sort that differs from
 * the starting choices (including an explicit `all` or `any`), a later page, or an MLS® number search.
 */
export function isFiltered(filters: Filters, defaults: SearchDefaults = NO_DEFAULTS): boolean {
    return toQuery(filters, {}, defaults).size > 0;
}

/** "Clear filters": every listing. The starting choices are removed with `all` and `any`; the sort stays. */
export function clearedFilters(filters: Filters): Filters {
    return {sort: filters.sort, page: 1};
}

/** How many pages there are for `total` matches, never more than the API will serve. */
export function pageCount(total: number): number {
    return Math.max(1, Math.min(MAX_PAGE, Math.ceil(Math.min(total, MAX_RESULTS) / PAGE_SIZE)));
}

/** "$799,900" from the API's "799900.00"; null for no price. */
export function formatPrice(price: string | null): string | null {
    if (price === null || price === "") return null;
    const n = Number(price);
    if (!Number.isFinite(n)) return null;
    return `$${Math.round(n).toLocaleString("en-CA")}`;
}

/** Path names of the API's other listing routes; they are never listing keys. */
const RESERVED_KEYS = ["search", "areas", "keys"];

/** True when `key` can be a listing key: plain characters, and not one of the API's route names. */
export function isListingKey(key: string): boolean {
    return /^[A-Za-z0-9_-]{1,32}$/.test(key) && !RESERVED_KEYS.includes(key.toLowerCase());
}

/**
 * True for an address that may be used as an image source: an absolute http(s) URL, or a
 * site-relative path (one leading slash; `//` and `/\` are protocol-relative and rejected).
 */
export function isPhotoUrl(value: string): boolean {
    if (value.startsWith("/")) return !value.startsWith("//") && !value.startsWith("/\\");
    try {
        const {protocol} = new URL(value);
        return protocol === "https:" || protocol === "http:";
    } catch {
        return false;
    }
}

/** "$4,210.55" from the API's "4210.55" (cents kept, for amounts such as property tax); null for none. */
export function formatMoney(amount: string | null | undefined): string | null {
    if (amount === null || amount === undefined || amount === "") return null;
    const n = Number(amount);
    if (!Number.isFinite(n)) return null;
    return `$${n.toLocaleString("en-CA", {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
}
